import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import {
  DEFAULT_DISCORD_INVITE_URL,
  getDiscordInviteUrlFromPayload,
} from "../../../utils/discordInviteUrl";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  input: {
    minWidth: 420,
    flex: 1,
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

export default function DiscordInviteSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discordInviteUrl, setDiscordInviteUrl] = useState(DEFAULT_DISCORD_INVITE_URL);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/discord-invite/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load Discord invite');
      if (!isMountedRef.current) return;
      setDiscordInviteUrl(getDiscordInviteUrlFromPayload(data));
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/discord-invite/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discordInviteUrl })
      });
      const data = await res.json();
      if (!data?.ok) {
        throw new Error(
          data?.err || 'Enter a valid Discord invite URL (discord.gg or discord.com/invite).'
        );
      }
      if (!isMountedRef.current) return;
      setDiscordInviteUrl(getDiscordInviteUrlFromPayload(data));
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      setSuccess(
        'Saved. New page loads and outgoing emails will use this invite. If settings are unavailable, Ouro falls back to the built-in default invite.'
      );
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, discordInviteUrl, isMountedRef, userId]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Discord Invite</Typography>
          <Typography variant="body2" color="textSecondary">
            Update the public Discord invite here when the current invite expires. Public pages,
            admin legal pages, and new outgoing emails will all use the saved value.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <TextField
            className={classes.input}
            label="Discord invite URL"
            variant="outlined"
            size="small"
            value={discordInviteUrl}
            onChange={(e) => setDiscordInviteUrl(e.target.value)}
            disabled={loading || saving || !userId}
            helperText={updatedAtMs ? `Last updated: ${new Date(updatedAtMs).toISOString()}` : 'Using built-in fallback until changed'}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={loading || saving || !userId}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>

          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}
      </Collapse>
    </Paper>
  );
}
